import { decodeName } from './decodeName';
import {
  DIFFICULTY_ORDER,
  difficultyLabel,
  difficultyTone,
  formatLen,
  mapIntent,
  mapPurpose,
  phraseWordCount
} from './seoMetrics';
import type {
  ClusterModeId,
  ClusterTableRow,
  HierarchicalNode,
  ReclusterResult,
  VolumeThresholds
} from '../types';

export const CLUSTER_MODES: Array<{
  id: ClusterModeId;
  label: string;
  icon: string;
}> = [
  { id: 'default', label: 'کلاستر معنایی', icon: 'diagram-3' },
  { id: 'intent', label: 'بر اساس نیت', icon: 'bullseye' },
  { id: 'difficulty', label: 'بر اساس سختی', icon: 'speedometer2' },
  { id: 'volume', label: 'بر اساس میزان جستجو', icon: 'bar-chart' },
  { id: 'len', label: 'بر اساس طول عبارت', icon: 'text-paragraph' },
  { id: 'purpose', label: 'بر اساس هدف', icon: 'tags' }
];

type LeafGroup = {
  label: string;
  leaves: HierarchicalNode[];
};

function collectLeaves(
  node: HierarchicalNode | null | undefined,
  list: HierarchicalNode[] = []
): HierarchicalNode[] {
  if (!node) return list;
  const children = node.children ?? [];
  if (!children.length) {
    list.push(node);
    return list;
  }
  children.forEach(child => collectLeaves(child, list));
  return list;
}

function cloneLeaf(leaf: HierarchicalNode): HierarchicalNode {
  const len =
    leaf.len == null || Number.isNaN(Number(leaf.len))
      ? phraseWordCount(leaf.name)
      : Number(leaf.len);

  return {
    name: leaf.name,
    search_volume_miztols: leaf.search_volume_miztols ?? 0,
    difficulty: leaf.difficulty ?? null,
    len,
    intent: Array.isArray(leaf.intent) ? [...leaf.intent] : [],
    purpose: Array.isArray(leaf.purpose) ? [...leaf.purpose] : []
  };
}

function leafLen(leaf: HierarchicalNode): number {
  if (leaf.len != null && !Number.isNaN(Number(leaf.len))) return Number(leaf.len);
  return phraseWordCount(leaf.name);
}

function volumeBucket(
  volume: number,
  lowMax: number,
  midMax: number
): 'low' | 'medium' | 'high' {
  if (volume <= lowMax) return 'low';
  if (volume <= midMax) return 'medium';
  return 'high';
}

function volumeThresholds(leaves: HierarchicalNode[]): VolumeThresholds {
  const vols = leaves
    .map(leaf => Number(leaf.search_volume_miztols) || 0)
    .sort((a, b) => a - b);
  if (!vols.length) return { lowMax: 0, midMax: 0 };
  const q1 = vols[Math.floor((vols.length - 1) / 3)];
  const q2 = vols[Math.floor(((vols.length - 1) * 2) / 3)];
  return { lowMax: q1, midMax: Math.max(q2, q1) };
}

const VOLUME_LABELS = {
  low: 'جستجوی کم',
  medium: 'جستجوی متوسط',
  high: 'جستجوی زیاد'
} as const;

const VOLUME_ORDER = ['high', 'medium', 'low'] as const;

function buildGroupedTree(
  groups: Map<string, LeafGroup>,
  order: string[] = []
): HierarchicalNode {
  const keys = order.length
    ? order.filter(key => groups.has(key))
    : [...groups.keys()].sort((a, b) => a.localeCompare(b, 'fa'));

  return {
    name: 'اصلی',
    children: keys.map(key => {
      const group = groups.get(key)!;
      return {
        name: group.label,
        children: group.leaves
          .slice()
          .sort((a, b) =>
            decodeName(a.name).localeCompare(decodeName(b.name), 'fa')
          )
          .map(cloneLeaf)
      };
    })
  };
}

export function buildTableFromTree(
  root: HierarchicalNode | null | undefined
): ClusterTableRow[] {
  const rows: ClusterTableRow[] = [];

  function walk(node: HierarchicalNode, path: string[] = []): void {
    const label = node.name;
    const next =
      label === 'main' || label === 'اصلی' || decodeName(label) === 'اصلی'
        ? path
        : [...path, label];
    const children = node.children ?? [];
    if (!children.length) {
      if (next.length) rows.push({ name: next });
      return;
    }
    children.forEach(child => walk(child, next));
  }

  if (root) walk(root);
  return rows;
}

/**
 * Rebuild hierarchical + table by SEO dimension.
 * Multi-value intent/purpose: keyword is placed in each matching bucket.
 * Table rows are always derived from the resulting hierarchical tree.
 */
export function reclusterByMode(
  hierarchical: HierarchicalNode | null | undefined,
  mode: ClusterModeId | string = 'default'
): ReclusterResult {
  if (!hierarchical) {
    return {
      hierarchical: null,
      table: [],
      mode,
      summary: null
    };
  }

  if (mode === 'default') {
    return {
      hierarchical,
      table: buildTableFromTree(hierarchical),
      mode,
      summary: null
    };
  }

  const leaves = collectLeaves(hierarchical);
  const groups = new Map<string, LeafGroup>();

  const ensureGroup = (key: string, label: string): LeafGroup => {
    if (!groups.has(key)) groups.set(key, { label, leaves: [] });
    return groups.get(key)!;
  };

  if (mode === 'intent') {
    leaves.forEach(leaf => {
      const intents = leaf.intent?.length ? leaf.intent : ['unknown'];
      intents.forEach(code => {
        const meta =
          code === 'unknown'
            ? { label: 'بدون نیت', short: '?' }
            : mapIntent(code);
        ensureGroup(code, `نیت: ${meta.label} (${meta.short})`).leaves.push(leaf);
      });
    });
    const order = ['I', 'C', 'T', 'N', 'unknown'];
    const hierarchicalNext = buildGroupedTree(groups, order);
    return {
      hierarchical: hierarchicalNext,
      table: buildTableFromTree(hierarchicalNext),
      mode,
      summary: `${leaves.length} کلمه در ${groups.size} گروه نیت`
    };
  }

  if (mode === 'difficulty') {
    leaves.forEach(leaf => {
      const tone =
        leaf.difficulty == null ? 'unknown' : difficultyTone(leaf.difficulty);
      const label =
        tone === 'unknown'
          ? 'سختی نامشخص'
          : `سختی: ${difficultyLabel(leaf.difficulty)}`;
      ensureGroup(tone, label).leaves.push(leaf);
    });
    const hierarchicalNext = buildGroupedTree(groups, [
      ...DIFFICULTY_ORDER,
      'unknown'
    ]);
    return {
      hierarchical: hierarchicalNext,
      table: buildTableFromTree(hierarchicalNext),
      mode,
      summary: `${leaves.length} کلمه در ${groups.size} سطح سختی`
    };
  }

  if (mode === 'volume') {
    const { lowMax, midMax } = volumeThresholds(leaves);
    const volumeGroupLabels = {
      low: `${VOLUME_LABELS.low} (≤ ${lowMax})`,
      medium: `${VOLUME_LABELS.medium} (≤ ${midMax})`,
      high: `${VOLUME_LABELS.high} (> ${midMax})`
    };
    leaves.forEach(leaf => {
      const vol = Number(leaf.search_volume_miztols) || 0;
      const bucket = volumeBucket(vol, lowMax, midMax);
      ensureGroup(bucket, volumeGroupLabels[bucket]).leaves.push(leaf);
    });
    const hierarchicalNext = buildGroupedTree(groups, [...VOLUME_ORDER]);
    return {
      hierarchical: hierarchicalNext,
      table: buildTableFromTree(hierarchicalNext),
      mode,
      summary: null,
      thresholds: { lowMax, midMax }
    };
  }

  if (mode === 'len') {
    leaves.forEach(leaf => {
      const n = leafLen(leaf);
      const key = n > 0 ? String(n) : 'unknown';
      const label = n > 0 ? `طول: ${formatLen(n)}` : 'طول نامشخص';
      ensureGroup(key, label).leaves.push(leaf);
    });
    const order = [...groups.keys()]
      .filter(key => key !== 'unknown')
      .sort((a, b) => Number(a) - Number(b));
    if (groups.has('unknown')) order.push('unknown');
    const hierarchicalNext = buildGroupedTree(groups, order);
    return {
      hierarchical: hierarchicalNext,
      table: buildTableFromTree(hierarchicalNext),
      mode,
      summary: `${leaves.length} عبارت در ${groups.size} گروه طول`
    };
  }

  if (mode === 'purpose') {
    leaves.forEach(leaf => {
      const purposes = leaf.purpose?.length ? leaf.purpose : ['unknown'];
      purposes.forEach(purpose => {
        const label =
          purpose === 'unknown' ? 'هدف نامشخص' : `هدف: ${mapPurpose(purpose)}`;
        ensureGroup(purpose, label).leaves.push(leaf);
      });
    });
    const hierarchicalNext = buildGroupedTree(groups);
    return {
      hierarchical: hierarchicalNext,
      table: buildTableFromTree(hierarchicalNext),
      mode,
      summary: `${leaves.length} کلمه در ${groups.size} گروه هدف`
    };
  }

  return {
    hierarchical,
    table: null,
    mode: 'default',
    summary: null
  };
}
